import { Divider, Form, FormInstance, Radio, Space, Typography } from 'antd';
import { ReactNode, useState } from 'react';
import { ReplyTimeForm } from '../../Components/Forms/ReplyTimeForm';
import { formatDateRangeWithDateString } from '../../Helpers/DateHelpers';
import { getCurrentLanguage } from '../../Helpers/RouteHelper';
import { householdMember, householdPersonAnswer } from '../../Services/PublicSiteService';
import { TextService } from '../../Services/TextService';
import { EventType } from '../../Types/EventType';
import { TimeChoice } from '../../Types/TimeChoice';
import { isOnlyComing } from '../../Types/AnswerOptions';

// The answers of each person are kept in the form under people.<memberId>.<timeId>, and the chosen
// time under people.<memberId>.single when only one time can be chosen.
const answerField = (memberId: number, key: number | string) => ["people", String(memberId), String(key)];

const isSingleChoiceEvent = (event: any) =>
    event.eventType !== EventType.Poll && event.timeChoice === TimeChoice.One && event.times?.length > 1;

// With only "Kommer" and one time there is nothing to choose for each person: being checked is the sign-up.
const isOneTimeSignUp = (event: any) => isOnlyComing(event) && event.times?.length === 1;

const hasAnswer = (person: householdMember | undefined, time: any, answerType: number) =>
    person?.replyTime.some(r => r.timeId === time.id && r.customerAnswerType === answerType) === true;

// A time the person already has a place in is never full for that person.
const isFullyBookedFor = (person: householdMember | undefined, time: any) =>
    time.isFullyBooked === true && !hasAnswer(person, time, 0);

// A person who already is on the waitlist stays there, also when the waitlist has become full.
const isWaitlistOpenFor = (person: householdMember | undefined, time: any) =>
    isFullyBookedFor(person, time) && (time.isWaitlistOpen === true || hasAnswer(person, time, 3));

/**
 * Fills in the answers each person in the household already has.
 */
export const setHouseholdAnswers = (form: FormInstance, event: any, people: householdMember[]) => {
    const isSignUp = event.eventType !== EventType.Poll;

    people.filter(person => person.replyId > 0).forEach(person => {
        person.replyTime.forEach(replyTime => {
            // "Maybe" is no longer an answer for a sign-up, so an old one has to be answered again.
            if (isSignUp && replyTime.customerAnswerType === 1) {
                return;
            }

            form.setFieldValue(answerField(person.memberId, replyTime.timeId), replyTime.customerAnswerType.toString());
        });

        if (isSingleChoiceEvent(event)) {
            const chosen = person.replyTime.find(r => r.customerAnswerType === 0 || r.customerAnswerType === 3);
            // With only "Kommer" there is no "Kommer inte" to choose, so a cancelled person starts unanswered.
            form.setFieldValue(answerField(person.memberId, "single"), chosen ? chosen.timeId : isOnlyComing(event) ? undefined : "none");
        }
    });
};

/**
 * A person that is checked without answers of their own starts with the answers of someone else who is
 * checked, since the family often answers the same. They can then be changed for each person. A place in
 * a time that is full for the person becomes the waitlist, or is left unanswered when the waitlist is closed.
 */
export const fillNewlyCheckedAnswers = (form: FormInstance, event: any, household: householdMember[], memberIds: number[]) => {
    const keys: (number | string)[] = [...(event.times ?? []).map((time: any) => time.id), "single"];
    const hasAnswers = (memberId: number) => keys.some(key => form.getFieldValue(answerField(memberId, key)) != null);
    const source = memberIds.find(hasAnswers);

    if (source == null) {
        return;
    }

    memberIds.filter(memberId => !hasAnswers(memberId)).forEach(memberId => {
        const person = household.find(p => p.memberId === memberId);

        keys.forEach(key => {
            let answer = form.getFieldValue(answerField(source, key));
            const time = (event.times ?? []).find((t: any) => t.id === key);

            if (time != null && (answer === "0" || answer === "3")) {
                answer = !isFullyBookedFor(person, time) ? "0" : isWaitlistOpenFor(person, time) ? "3" : undefined;
            }

            form.setFieldValue(answerField(memberId, key), answer);
        });
    });
};

/**
 * The answers to save for each chosen person. With one time to pick, the chosen time is a sign-up
 * (or the waitlist when it is full) and the others a "no". With only "Kommer", an unanswered time is a "no".
 */
export const getHouseholdAnswers = (form: FormInstance, event: any, household: householdMember[], memberIds: number[]): householdPersonAnswer[] => {
    return household.filter(person => memberIds.includes(person.memberId)).map(person => {
        const single = form.getFieldValue(answerField(person.memberId, "single"));

        const timeAnswers = (event.times ?? [])
            .map((time: any) => {
                const answer = isSingleChoiceEvent(event)
                    ? (single === time.id ? (isFullyBookedFor(person, time) ? "3" : "0") : "2")
                    // With only "Kommer" and one time, a checked person is signed up.
                    : isOneTimeSignUp(event) ? (isFullyBookedFor(person, time) ? "3" : "0")
                        : form.getFieldValue(answerField(person.memberId, time.id)) ?? (isOnlyComing(event) ? "2" : undefined);

                return answer != null ? { timeId: time.id, customerAnswer: Number(answer) } : null;
            })
            .filter((answer: any) => answer != null);

        return { memberId: person.memberId, timeAnswers: timeAnswers };
    });
};

/**
 * After a 409, drops the answers that ask for a place that is no longer free, so they have to be answered again.
 */
export const clearFullHouseholdAnswers = (form: FormInstance, event: any, household: householdMember[]) => {
    household.forEach(person => {
        (event.times ?? []).forEach((time: any) => {
            const answer = form.getFieldValue(answerField(person.memberId, time.id));
            if ((answer === "0" && isFullyBookedFor(person, time)) || (answer === "3" && !isWaitlistOpenFor(person, time))) {
                form.setFieldValue(answerField(person.memberId, time.id), undefined);
            }
        });

        form.setFieldValue(answerField(person.memberId, "single"), undefined);
    });
};

/**
 * The answers for each person a primary member signs up, one section per checked person.
 */
export const HouseholdAnswers = (props: { form: FormInstance, event: any, household: householdMember[], timeZoneHint?: ReactNode }) => {
    const GetText = TextService.GetText;
    const [, setTick] = useState(0);
    const memberIds: number[] = Form.useWatch("memberIds", props.form) ?? [];
    const event = props.event;
    const isSe = getCurrentLanguage() === "se";
    const people = props.household.filter(person => memberIds.includes(person.memberId));

    const setAnswer = (memberId: number, timeId: number, answer: string) => {
        props.form.setFieldValue(answerField(memberId, timeId), answer);
        setTick(tick => tick + 1);
    };

    return <>
        {people.map((person, index) =>
            <div key={person.memberId} data-testid={"household-answers-" + person.memberId}>
                <Divider orientation="left" plain style={{ marginTop: 8 }}>
                    <Typography.Text strong>{person.name}</Typography.Text>
                </Divider>

                {isSingleChoiceEvent(event) ?
                    <Form.Item
                        label={<>{GetText("Event_ChooseTime")}{props.timeZoneHint}</>}
                        name={answerField(person.memberId, "single")}
                        rules={[{ required: true, message: GetText("Event_PlaseAnswerAvailabilityValidationMessage") }]}
                    >
                        <Radio.Group onChange={() => setTick(tick => tick + 1)}>
                            <Space direction="vertical" style={{ textAlign: "left" }}>
                                {event.times.map((time: any) =>
                                    <Radio key={time.id} value={time.id} disabled={isFullyBookedFor(person, time) && !isWaitlistOpenFor(person, time)}>
                                        {formatDateRangeWithDateString(time.start, time.end, time.isFullDay, isSe)}
                                        {isFullyBookedFor(person, time) ? <i> &ndash; {GetText(!isWaitlistOpenFor(person, time) ? "Event_TimeFullyBooked" : hasAnswer(person, time, 3) ? "Event_OnWaitlist" : "Event_FullJoinWaitlist")}</i> : ""}
                                    </Radio>
                                )}
                                {!isOnlyComing(event) ? <Radio value="none">{GetText("Event_NotComing")}</Radio> : null}
                            </Space>
                        </Radio.Group>
                    </Form.Item>
                    : event.times?.map((time: any) =>
                        <Form.Item
                            // One time is told in the event header, so each person's row is the answer.
                            label={event.times.length === 1 ? (isOneTimeSignUp(event) ? " " : GetText("Event_PersonAnswer"))
                                : <>{formatDateRangeWithDateString(time.start, time.end, time.isFullDay, isSe)}{props.timeZoneHint}</>}
                            name={answerField(person.memberId, time.id)}
                            key={time.id}
                            rules={[{ required: !isFullyBookedFor(person, time) && !isOnlyComing(event), message: GetText("Event_PlaseAnswerAvailabilityValidationMessage") }]}
                        >
                            <ReplyTimeForm
                                event={event}
                                // The list of other participants is shown once, under the first person.
                                time={index === 0 ? time : { ...time, participants: null }}
                                onChangeAnswerComplete={(timeId: number, answer: string) => setAnswer(person.memberId, timeId, answer)}
                                answer={props.form.getFieldValue(answerField(person.memberId, time.id))}
                                isFullyBooked={isFullyBookedFor(person, time)}
                                isWaitlistOpen={isWaitlistOpenFor(person, time)}
                                isOnWaitlist={hasAnswer(person, time, 3)}
                                hideAnswers={isOneTimeSignUp(event)} />
                        </Form.Item>
                    )}
            </div>
        )}
    </>;
};
